import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Meal } from '@/components/nutrition/mealColumns'
import MealPlanEditor, { MealPlan } from '@/components/nutrition/MealPlanEditor'
import { Slash } from 'lucide-react'
import AlertBlocker from '@/components/AlertBlocker'
import DeleteAlert from '@/components/DeleteAlert'
import { errorToast } from '@/lib/utils/ToastUtils'
import { useNavbar } from '@/components/navbar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { toast } from 'sonner'
import { useDocumentTitle } from '@/lib/hooks/useDocumentTitle'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { patchListCache, removeFromList } from '@trainrr/api-client'
import LoadingSpinner from '@/components/LoadingSpinner'

const sortMealsByNumber = (meals: Meal[]) =>
  [...meals].sort((a, b) => (a.meal_number ?? 0) - (b.meal_number ?? 0))

type NutritionPlan = Awaited<
  ReturnType<typeof trpcUtils.nutrition.getPlan.ensureData>
>

export const Route = createFileRoute('/_auth/_dashboard/nutrition/$templateId')(
  {
    component: MealPlanTemplateEditor,
    loader: ({ params }) =>
      trpcUtils.nutrition.getPlan.ensureData({
        planId: params.templateId,
      }),
  },
)

function MealPlanTemplateEditor() {
  const { templateId } = Route.useParams()
  const { data: plan } = trpc.nutrition.getPlan.useQuery({
    planId: templateId,
  })
  if (!plan) return <LoadingSpinner />
  return <MealPlanTemplateEditorForm key={plan.id} plan={plan} />
}

function MealPlanTemplateEditorForm({ plan }: { plan: NutritionPlan }) {
  const { setContent } = useNavbar()
  const navigate = useNavigate()
  const utils = trpc.useUtils()
  const linkedCount = plan.linkedCount
  const deletePlan = trpc.nutrition.deletePlan.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.nutrition.listTemplates, (old) =>
        removeFromList(old, variables.planId),
      )
    },
  })

  const initialPlan = useMemo<MealPlan | null>(() => {
    if (!plan) return null
    return {
      id: plan.id,
      name: plan.name,
      is_primary: false,
      is_visible: true,
      is_template: true,
      meals: sortMealsByNumber(plan.meals ?? []),
    }
  }, [plan])

  const [mealPlan, setMealPlan] = useState<MealPlan | null>(initialPlan)
  const [hasChanges, setHasChanges] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  useDocumentTitle(mealPlan?.name || 'Meal plan')

  useEffect(() => {
    setMealPlan(initialPlan)
  }, [initialPlan])

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to="/nutrition">Nutrition</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <Slash />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{mealPlan?.name || 'Template'}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    [mealPlan?.name],
  )

  useEffect(() => {
    setContent(breadcrumb)
    return () => setContent(null)
  }, [breadcrumb, setContent])

  if (!mealPlan) {
    return null
  }

  const handleDelete = async () => {
    if (!mealPlan?.id) return
    try {
      await deletePlan.mutateAsync({ planId: mealPlan.id })
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      return
    }
    toast.success('Template deleted')
    setHasChanges(false)
    navigate({ to: '/nutrition' })
  }

  return (
    <div className="flex w-full flex-col">
      <AlertBlocker shouldBlock={hasChanges} />
      <MealPlanEditor
        key={plan?.id}
        mealPlan={mealPlan}
        onSaved={setMealPlan}
        onDuplicated={(duplicatedPlan) => {
          setHasChanges(false)
          // Delay navigation to let the dialog close animation finish (duration-200)
          setTimeout(() => {
            navigate({
              to: '/nutrition/$templateId',
              params: { templateId: duplicatedPlan.id },
            })
          }, 250)
        }}
        hasChanges={hasChanges}
        setHasChanges={setHasChanges}
        mode="template"
        onDelete={() => setDeleteOpen(true)}
      />
      <DeleteAlert
        title={`Delete ${mealPlan.name}?`}
        description={`This will remove the plan from ${linkedCount} ${
          linkedCount === 1 ? 'client' : 'clients'
        } that are linked. Are you sure you want to delete this meal plan template?`}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onDelete={handleDelete}
      />
    </div>
  )
}
